import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import ValuesStore from "../../../store/values-store";
import useAppraisal from "../../../hooks/academics/useAppraisal";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import AppraisalConfiguration from "./AppraisalConfiguration";
import AppraisalRecordDrawer from "./AppraisalRecordDrawer";
import AppraisalRuns from "./AppraisalRuns";
import {
  ACTION_LABELS,
  STATUSES,
  allowedActions,
  appraisalRoles,
  listFrom,
  scoreText,
} from "./appraisalUtils";

const dateText = (value) =>
  value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—";
const statusTag = (value) => (
  <Tag
    color={
      {
        in_progress: "default",
        deputy_head_teacher_review: "gold",
        head_teacher_review: "blue",
        approved: "green",
      }[value]
    }
  >
    {STATUSES.find((item) => item.value === value)?.label || value}
  </Tag>
);
const requiredText = [
  { required: true, whitespace: true, message: "Enter a reason." },
];
const blockedKindLabels = {
  open_case: "Open case",
  undelivered_material: "Undelivered material",
  unrecorded_participation: "Unrecorded participation",
};
const normalizeBlocker = (item, index) => {
  const kind = item?.kind;
  const itemId = item?.item_id ?? item?.itemId;
  const staffId = item?.staff_id ?? item?.staffId;
  const staff =
    item?.staff_name ??
    item?.staffName ??
    (staffId !== undefined && staffId !== null
      ? `Staff #${staffId}`
      : "Unknown staff");
  const kindLabel = blockedKindLabels[kind] || kind || "Blocked";
  const detailPart =
    item?.detail || (itemId !== undefined ? `Item #${itemId}` : "");
  return {
    kind,
    itemId,
    key: `${kind}::${itemId}::${index}`,
    label: `${staff} — ${kindLabel} — ${detailPart}`,
  };
};

export default function AppraisalManagement() {
  const request = useAppraisal();
  const store = ValuesStore();
  const user = store.getValue("user") || {};
  const roles = appraisalRoles(store.getValue("myRoles"), user);
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState();
  const [cycleError, setCycleError] = useState("");
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [recordId, setRecordId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [runRefresh, setRunRefresh] = useState(0);
  const [runBusy, setRunBusy] = useState(false);
  const [cycleRefresh, setCycleRefresh] = useState(0);
  const [bulkResult, setBulkResult] = useState(null);
  const [action, setAction] = useState(null);
  const [cycleAction, setCycleAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [blockerOptions, setBlockerOptions] = useState([]);
  const [blockersLoading, setBlockersLoading] = useState(false);
  const [actionForm] = Form.useForm();
  const [cycleForm] = Form.useForm();
  const force = Form.useWatch("force", cycleForm);
  const overrideItems = Form.useWatch("overrideItems", cycleForm);
  const watchedTermId = Form.useWatch("term_id", cycleForm);
  const selectedCycle = cycles.find((cycle) => cycle.id === cycleId);
  const refreshRecords = useCallback(
    () => setRefreshKey((value) => value + 1),
    [],
  );
  const onRunCompleted = useCallback(() => {
    setRefreshKey((value) => value + 1);
    setCycleRefresh((value) => value + 1);
  }, []);

  useEffect(() => {
    if (
      cycleAction !== "initialize" &&
      cycleAction !== "recompute-snapshots"
    )
      return undefined;
    if (!cycleId) {
      setBlockerOptions([]);
      return undefined;
    }
    let cancelled = false;
    setBlockersLoading(true);
    request(
      "GET",
      `cycles/${cycleId}/readiness`,
      undefined,
      watchedTermId ? { term_id: watchedTermId } : undefined,
    )
      .then((data) => {
        if (cancelled) return;
        const blocked = Array.isArray(data?.blocked) ? data.blocked : [];
        setBlockerOptions(
          blocked
            .map(normalizeBlocker)
            .filter(
              (option) =>
                option.kind &&
                option.itemId !== undefined &&
                option.itemId !== null,
            ),
        );
      })
      .catch(() => {
        if (!cancelled) setBlockerOptions([]);
      })
      .finally(() => {
        if (!cancelled) setBlockersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cycleAction, cycleId, watchedTermId, request]);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);
  useEffect(() => {
    let cancelled = false;
    setCyclesLoading(true);
    request("GET", "cycles")
      .then((data) => {
        if (cancelled) return;
        const next = listFrom(data);
        setCycles(next);
        setCycleError("");
        setCycleId((current) =>
          next.some((item) => item.id === current) ? current : next[0]?.id,
        );
      })
      .catch((err) => {
        if (!cancelled) setCycleError(err.message);
      })
      .finally(() => {
        if (!cancelled) setCyclesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [request, cycleRefresh]);

  useEffect(() => {
    let cancelled = false;
    setSelectedIds([]);
    setRecords([]);
    setCount(0);
    setError("");
    if (!cycleId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    request("GET", "records", undefined, {
      cycle: cycleId,
      ...filters,
      page: pagination.page,
      pageSize: pagination.pageSize,
    })
      .then((data) => {
        if (cancelled) return;
        const rows = listFrom(data);
        setRecords(rows);
        setCount(
          Array.isArray(data) ? rows.length : (data?.count ?? rows.length),
        );
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [request, cycleId, filters, pagination, refreshKey]);

  const selectedRecords = records.filter((record) =>
    selectedIds.includes(record.id),
  );
  const actions = useMemo(
    () =>
      Object.keys(ACTION_LABELS).filter(
        (key) =>
          selectedRecords.length &&
          selectedRecords.every((record) =>
            allowedActions(
              record,
              roles,
              selectedCycle?.status === "Locked",
            ).includes(key),
          ),
      ),
    [selectedRecords, roles, selectedCycle?.status],
  );
  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPagination((previous) => ({ ...previous, page: 1 }));
  };
  const openAction = (value) => {
    actionForm.resetFields();
    setActionError("");
    setAction(value);
  };
  const openCycleAction = (value) => {
    cycleForm.resetFields();
    setActionError("");
    setCycleAction(value);
    if (value === "edit")
      cycleForm.setFieldsValue({ name: selectedCycle?.name });
    if (
      value === "recompute-snapshots" &&
      selectedCycle?.status === "Locked" &&
      roles.admin
    )
      cycleForm.setFieldsValue({ force: true });
  };

  const submitAction = async () => {
    let values;
    try {
      values = await actionForm.validateFields();
    } catch {
      return;
    }
    if (
      !selectedRecords.length ||
      !selectedRecords.every((record) =>
        allowedActions(
          record,
          roles,
          selectedCycle?.status === "Locked",
        ).includes(action),
      )
    )
      return;
    setSaving(true);
    setActionError("");
    const body = { appraisalRecordIds: [...selectedIds] };
    let endpoint = "advance";
    if (action === "flag" || action === "unflag") {
      endpoint = "flag";
      body.flag = action === "flag";
      if (body.flag) body.flagReason = values.flagReason.trim();
    } else {
      if (values.remarks?.trim()) body.remarks = values.remarks.trim();
      if (action === "final-approve") {
        endpoint = "final-approve";
        body.promotionFlag = !!values.promotionFlag;
        body.trainingFlag = !!values.trainingFlag;
        body.finalRemarks = values.finalRemarks?.trim() || "";
      } else body.action = action;
    }
    try {
      const result = await request("POST", `records/${endpoint}`, body);
      setBulkResult(result);
      setSelectedIds([]);
      setAction(null);
      refreshRecords();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const submitCycleAction = async () => {
    let values;
    try {
      values = await cycleForm.validateFields();
    } catch {
      return;
    }
    if (!roles.admin) return;
    setSaving(true);
    setActionError("");
    try {
      if (cycleAction === "create" || cycleAction === "edit") {
        const body = {
          name: values.name.trim(),
          ...(user.id ? { createdById: user.id } : {}),
        };
        if (cycleAction === "edit") body.id = cycleId;
        if (values.academicYearId) body.academicYearId = values.academicYearId;
        // POST upsert requires the write-only academic-year ID on edits too.
        if (cycleAction === "create") body.status = "Active";
        const cycle = await request("POST", "cycles", body);
        setCycles((previous) => [
          cycle,
          ...previous.filter((item) => item.id !== cycle.id),
        ]);
        setCycleId(cycle.id);
        message.success("Cycle saved");
      } else if (cycleAction === "lock") {
        await request("POST", `cycles/${cycleId}/lock`, {});
        message.success("Cycle locked");
      } else {
        const body = {};
        if (values.term_id) body.term_id = values.term_id;
        if (values.remarks?.trim()) body.remarks = values.remarks.trim();
        if (cycleAction === "recompute-snapshots")
          body.force = roles.admin && !!values.force;
        const sharedReason = values.overrideReason?.trim();
        const overrides = (values.overrideItems || [])
          .map((key) => {
            const option = blockerOptions.find((item) => item.key === key);
            return option
              ? { kind: option.kind, itemId: option.itemId, reason: sharedReason }
              : null;
          })
          .filter(
            (item) =>
              item &&
              item.kind &&
              item.itemId !== undefined &&
              item.itemId !== null &&
              item.reason,
          );
        if (overrides.length) body.overrides = overrides;
        const run = await request(
          "POST",
          `cycles/${cycleId}/${cycleAction}`,
          body,
        );
        setRunBusy(["Pending", "Running"].includes(run?.status));
        message.info(
          run?.status === "Completed"
            ? "Operation completed"
            : "Operation queued. Follow its progress in cycle activity.",
        );
        setRunRefresh((value) => value + 1);
      }
      setCycleAction(null);
      setCycleRefresh((value) => value + 1);
      refreshRecords();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const validateCycle = async () => {
    setSaving(true);
    try {
      const data = await request("GET", `cycles/${cycleId}/validate`);
      setReadiness({ valid: true, message: data.detail });
    } catch (err) {
      setReadiness({ valid: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const recordsTab = (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space wrap>
        <Select
          aria-label="Appraisal cycle"
          placeholder="Choose a cycle"
          style={{ minWidth: 250 }}
          loading={cyclesLoading}
          value={cycleId}
          options={cycles.map((cycle) => ({
            value: cycle.id,
            label: `${cycle.name} · ${cycle.academicYear}`,
          }))}
          onChange={(value) => {
            setCycleId(value);
            setSelectedIds([]);
            setReadiness(null);
            setBulkResult(null);
            setPagination((previous) => ({ ...previous, page: 1 }));
          }}
        />
        {selectedCycle && (
          <Tag color={selectedCycle.status === "Locked" ? "orange" : "green"}>
            {selectedCycle.status}
          </Tag>
        )}
        <Button
          onClick={() => {
            refreshRecords();
            setCycleRefresh((value) => value + 1);
          }}
          loading={loading || cyclesLoading}
        >
          Refresh
        </Button>
        {roles.admin && (
          <Button onClick={() => openCycleAction("create")}>New cycle</Button>
        )}
      </Space>
      {cycleError && (
        <Alert
          type="error"
          message="Could not load appraisal cycles"
          description={cycleError}
          showIcon
        />
      )}
      {roles.admin && selectedCycle && (
        <Space wrap>
          <Button disabled={runBusy} onClick={() => openCycleAction("edit")}>
            Edit cycle
          </Button>
          <Button loading={saving && !cycleAction} onClick={validateCycle}>
            Check readiness
          </Button>
          <Button
            disabled={runBusy || selectedCycle.status === "Locked"}
            onClick={() => openCycleAction("initialize")}
          >
            Initialize
          </Button>
          <Button
            disabled={
              runBusy || (selectedCycle.status === "Locked" && !roles.admin)
            }
            onClick={() => openCycleAction("recompute-snapshots")}
          >
            Recompute scores
          </Button>
          <Button
            danger
            disabled={runBusy || selectedCycle.status === "Locked"}
            onClick={() => openCycleAction("lock")}
          >
            Lock cycle
          </Button>
        </Space>
      )}
      {readiness && (
        <Alert
          type={readiness.valid ? "success" : "error"}
          message={readiness.message}
          showIcon
          closable
          onClose={() => setReadiness(null)}
        />
      )}
      <Alert
        type="info"
        showIcon
        message="Scores are computed automatically"
        description="Scores refresh when a cycle is initialized, recomputed, or through the weekly schedule. Source data changes may not appear until the next computation."
      />
      <Space wrap>
        <Input.Search
          aria-label="Search staff"
          placeholder="Search staff name"
          allowClear
          onSearch={(value) => updateFilter("search", value.trim())}
          style={{ width: 240 }}
        />
        <Select
          aria-label="Review status"
          placeholder="All statuses"
          allowClear
          value={filters.status}
          options={STATUSES}
          onChange={(value) => updateFilter("status", value)}
          style={{ width: 245 }}
        />
        <Select
          aria-label="Staff pathway"
          placeholder="All pathways"
          allowClear
          value={filters.staffCategory}
          options={[
            { value: "teaching", label: "Teaching" },
            { value: "non_teaching", label: "Non-Teaching" },
          ]}
          onChange={(value) => updateFilter("staffCategory", value)}
          style={{ width: 180 }}
        />
        {[
          ["isFlagged", "Flagged"],
          ["promotionFlag", "Promotion"],
          ["trainingFlag", "Training"],
        ].map(([key, label]) => (
          <Select
            key={key}
            aria-label={label}
            placeholder={label}
            allowClear
            value={filters[key]}
            onChange={(value) => updateFilter(key, value)}
            style={{ width: 130 }}
            options={[
              { value: "true", label: `${label}: Yes` },
              { value: "false", label: `${label}: No` },
            ]}
          />
        ))}
        <Select
          aria-label="Record ordering"
          value={filters.ordering || "-created_at"}
          onChange={(value) => updateFilter("ordering", value)}
          style={{ width: 180 }}
          options={[
            { value: "-created_at", label: "Newest first" },
            { value: "-api_score", label: "Highest score" },
            { value: "api_score", label: "Lowest score" },
            { value: "status", label: "Review status" },
          ]}
        />
      </Space>
      {error && (
        <Alert
          type="error"
          message="Could not load staff records"
          description={error}
          action={<Button onClick={refreshRecords}>Retry</Button>}
          showIcon
        />
      )}
      {bulkResult && (
        <Alert
          type={bulkResult.errors?.length ? "warning" : "success"}
          showIcon
          closable
          onClose={() => setBulkResult(null)}
          message={`${bulkResult.succeeded?.length || 0} record(s) updated; ${bulkResult.errors?.length || 0} could not be updated.`}
          description={
            bulkResult.errors?.length ? (
              <ul>
                {bulkResult.errors.map((item, index) => (
                  <li key={`${item.id}-${index}`}>
                    Record {item.id}: {item.error}
                  </li>
                ))}
              </ul>
            ) : null
          }
        />
      )}
      {!!selectedIds.length && (
        <Space wrap>
          <Typography.Text>{selectedIds.length} selected</Typography.Text>
          {roles.fiveRAssessor &&
            selectedRecords.some(
              (record) =>
                record.status === "in_progress" &&
                record.fiveRComplete !== true,
            ) && (
              <Typography.Text type="warning">
                Submit the annual 5R assessment for every selected In Progress
                record before submitting for review.
              </Typography.Text>
            )}
          {actions.map((key) => (
            <Button
              key={key}
              danger={key === "Returned" || key === "Reopened"}
              onClick={() => openAction(key)}
            >
              {ACTION_LABELS[key]}
            </Button>
          ))}
          {!actions.length && (
            <Typography.Text type="secondary">
              Workflow actions depend on the selected records' review stage,
              assessment completion, and your role.
            </Typography.Text>
          )}
        </Space>
      )}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={records}
        scroll={{ x: 1400 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: setSelectedIds,
        }}
        locale={{
          emptyText: cycleId
            ? "No staff appraisal records. Initialize this cycle to create records, or adjust your filters."
            : "Choose an appraisal cycle to view staff records.",
        }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: count,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setSelectedIds([]);
            setPagination({ page, pageSize });
          },
        }}
        columns={[
          {
            title: "Staff",
            dataIndex: "staffName",
            render: (name, record) => (
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => setRecordId(record.id)}
              >
                {name || "View record"}
              </Button>
            ),
          },
          { title: "Role", dataIndex: "roleSnapshot" },
          {
            title: "Pathway",
            dataIndex: "staffCategoryDisplay",
            render: (value, record) => (
              <Tag
                color={record.staffCategory === "teaching" ? "blue" : "purple"}
              >
                {value ||
                  (record.staffCategory === "teaching"
                    ? "Teaching"
                    : record.staffCategory === "non_teaching"
                      ? "Non-Teaching"
                      : "—")}
              </Tag>
            ),
          },
          {
            title: "Performance index",
            dataIndex: "apiScore",
            render: (value, record) => (
              <Space direction="vertical" size={0}>
                {scoreText(value)}
                {record.fiveRComplete !== true && (
                  <Tag color="orange">Provisional</Tag>
                )}
              </Space>
            ),
          },
          {
            title: "Annual 5R",
            dataIndex: "fiveRComplete",
            render: (complete) => (
              <Tag color={complete ? "green" : "orange"}>
                {complete ? "Complete" : "Pending"}
              </Tag>
            ),
          },
          {
            title: "Grade",
            dataIndex: "apiGrade",
            render: (value) => value || "—",
          },
          { title: "Status", dataIndex: "status", render: statusTag },
          {
            title: "Flags",
            render: (_, record) => (
              <Space wrap>
                {record.isFlagged && (
                  <Tag color="red" title={record.flagReason}>
                    Flagged
                  </Tag>
                )}
                {record.promotionFlag && <Tag color="green">Promotion</Tag>}
                {record.trainingFlag && <Tag color="blue">Training</Tag>}
              </Space>
            ),
          },
          {
            title: "Score updated",
            dataIndex: "lastScoreUpdatedAt",
            render: dateText,
          },
        ]}
      />
      <AppraisalRuns
        cycleId={cycleId}
        request={request}
        refreshKey={runRefresh}
        onCompleted={onRunCompleted}
        onBusyChange={setRunBusy}
      />
    </Space>
  );

  return (
    <Card>
      <Typography.Title level={3}>Academic Appraisal</Typography.Title>
      <Typography.Paragraph type="secondary">
        Review staff performance, supporting evidence, and approvals for each
        academic year.
      </Typography.Paragraph>
      <Tabs
        destroyInactiveTabPane
        items={[
          { key: "records", label: "Staff appraisals", children: recordsTab },
          ...(roles.admin
            ? [
                {
                  key: "configuration",
                  label: "Scoring configuration",
                  children: (
                    <AppraisalConfiguration
                      request={request}
                      userId={user.id}
                      canManageCriteria={roles.criteriaManager}
                      canPreviewFormulas={roles.criteriaManager}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />
      <AppraisalRecordDrawer
        key={recordId || "closed"}
        recordId={recordId}
        onClose={() => setRecordId(null)}
        request={request}
        canManageNotes={roles.hod}
        canManageFiveR={roles.fiveRAssessor}
        cycleId={cycleId}
        cycleLocked={selectedCycle?.status === "Locked"}
        terms={terms}
        onChanged={onRunCompleted}
      />
      <Modal
        title={`${ACTION_LABELS[action] || "Review"} · ${selectedIds.length} record(s)`}
        open={!!action}
        onCancel={() => !saving && setAction(null)}
        onOk={submitAction}
        confirmLoading={saving}
        okText={ACTION_LABELS[action] || "Confirm"}
        destroyOnClose
      >
        {actionError && <Alert type="error" message={actionError} showIcon />}
        <Typography.Paragraph>
          This action applies to every selected record.
        </Typography.Paragraph>
        <Form
          form={actionForm}
          layout="vertical"
          preserve={false}
          initialValues={{ promotionFlag: false, trainingFlag: false }}
        >
          {action === "flag" ? (
            <Form.Item
              name="flagReason"
              label="Reason for flagging"
              rules={requiredText}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          ) : (
            action !== "unflag" && (
              <Form.Item
                name="remarks"
                label="Remarks"
                rules={
                  action === "Returned" || action === "Reopened"
                    ? requiredText
                    : []
                }
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            )
          )}
          {action === "final-approve" && (
            <>
              <Form.Item name="promotionFlag" valuePropName="checked">
                <Checkbox>Recommend for promotion</Checkbox>
              </Form.Item>
              <Form.Item name="trainingFlag" valuePropName="checked">
                <Checkbox>Recommend training</Checkbox>
              </Form.Item>
              <Form.Item name="finalRemarks" label="Final remarks">
                <Input.TextArea rows={3} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
      <Modal
        title={
          {
            create: "New appraisal cycle",
            edit: "Edit appraisal cycle",
            initialize: "Initialize cycle",
            "recompute-snapshots": "Recompute scores",
            lock: "Lock cycle",
          }[cycleAction]
        }
        open={!!cycleAction}
        onCancel={() => !saving && setCycleAction(null)}
        onOk={submitCycleAction}
        confirmLoading={saving}
        okButtonProps={{ danger: cycleAction === "lock" || !!force }}
        okText={cycleAction === "lock" ? "Lock cycle" : "Continue"}
        destroyOnClose
      >
        {actionError && <Alert type="error" message={actionError} showIcon />}
        <Form
          form={cycleForm}
          layout="vertical"
          preserve={false}
          initialValues={{ force: false }}
        >
          {(cycleAction === "create" || cycleAction === "edit") && (
            <>
              <Form.Item
                name="name"
                label="Cycle name"
                rules={[{ required: true, whitespace: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="academicYearId"
                label="Academic year"
                rules={[
                  { required: true, message: "Select the academic year." },
                ]}
                extra={
                  cycleAction === "edit"
                    ? `Current year: ${selectedCycle?.academicYear}. Select the year to retain or replace it.`
                    : "Only one cycle may exist per academic year."
                }
              >
                <Select
                  options={academicYears.map((year) => ({
                    value: year.id,
                    label: year.acadyr,
                  }))}
                />
              </Form.Item>
            </>
          )}
          {(cycleAction === "initialize" ||
            cycleAction === "recompute-snapshots") && (
            <>
              <Form.Item
                name="term_id"
                label="Term"
                rules={
                  cycleAction === "recompute-snapshots"
                    ? [{ required: true, message: "Select a term." }]
                    : []
                }
              >
                <Select
                  allowClear
                  placeholder={
                    cycleAction === "initialize"
                      ? "Use current term"
                      : "Select term"
                  }
                  options={terms.map((term) => ({
                    value: term.id,
                    label: term.name,
                  }))}
                />
              </Form.Item>
              {cycleAction === "recompute-snapshots" && roles.admin && (
                <Form.Item name="force" valuePropName="checked">
                  <Checkbox>
                    Override locks and reopen affected approvals (System
                    Administrator)
                  </Checkbox>
                </Form.Item>
              )}
              {force && (
                <Alert
                  showIcon
                  type="warning"
                  message="Changed scores can reopen records that are already in review or approved."
                />
              )}
              <Form.Item
                name="remarks"
                label="Remarks"
                rules={force ? requiredText : []}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="About readiness overrides"
                description="An override lets this run skip a blocking item for this run only; the block returns next run. Overrides never persist and never resolve the underlying item. Each override needs a reason and is logged with who overrode it."
              />
              {blockerOptions.length > 0 || blockersLoading ? (
                <>
                  <Form.Item
                    name="overrideItems"
                    label="Blocking items (pick one or more)"
                    style={{ marginBottom: 8 }}
                  >
                    <Select
                      mode="multiple"
                      placeholder={
                        blockersLoading
                          ? "Loading blocking items\u2026"
                          : "Select blocking items to override"
                      }
                      loading={blockersLoading}
                      options={blockerOptions.map((option) => ({
                        value: option.key,
                        label: option.label,
                      }))}
                      optionFilterProp="label"
                      showSearch
                      listHeight={256}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="overrideReason"
                    label="Reason (applies to every selected item)"
                    dependencies={["overrideItems"]}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            (getFieldValue("overrideItems") || []).length &&
                            !String(value || "").trim()
                          ) {
                            return Promise.reject(new Error("Enter a reason."));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder="Why override these items for this run?"
                    />
                  </Form.Item>
                  {!!(overrideItems || []).length && (
                    <Typography.Text type="secondary">
                      Overrides apply to this run only. Each override
                      must match a live blocking item and include a
                      reason. The same reason is logged for every
                      selected item.
                    </Typography.Text>
                  )}
                </>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message="No blocking items found"
                  description="The readiness check found nothing to override for this term, so no overrides are needed. If blockers appear when the run executes, run again and pick the items from the list."
                />
              )}
            </>
          )}
          {cycleAction === "lock" && (
            <Alert
              showIcon
              type="warning"
              message="Lock this cycle and all component scores?"
              description="Future score changes will require a forced recomputation by a System Administrator, with a reason."
            />
          )}
        </Form>
      </Modal>
    </Card>
  );
}
